/**
 * SA provinces — the coarse "region" vocabulary for the geo-region MVP (web).
 *
 * A listing's seller region (`ListingDto.sellerRegion`) and a farmer's own
 * region (`/me/farm-profile.region`) are one of these 9 South African
 * provinces, or null/unset. This is intentionally COARSE — province-level, not
 * GPS — so a buyer can do a cheap client-side "near me" sort without exposing a
 * farm's precise location (POPIA-minded: the precise `farmGps` stays private).
 *
 * This list is the single source of truth shared by the three web surfaces that
 * touch region: the marketplace browse (card chip + buyer picker) and the
 * farm-profile editor. Keep the strings byte-identical to the backend contract
 * — the API stores/returns these exact values, so any drift would break the
 * card's "matches my region" comparison and the editor's round-trip.
 *
 * The values are stored verbatim (not translated) so the comparison is stable
 * across locales; only the picker's blank/"All" affordance is translated.
 */

/** The 9 SA provinces, in the conventional order. The canonical region values. */
export const SA_PROVINCES = [
  'Limpopo',
  'Mpumalanga',
  'Gauteng',
  'KwaZulu-Natal',
  'Eastern Cape',
  'Western Cape',
  'Northern Cape',
  'Free State',
  'North West',
] as const;

/** A single SA province string (one of {@link SA_PROVINCES}). */
export type SaProvince = (typeof SA_PROVINCES)[number];

/** True when `value` is exactly one of the 9 canonical province strings. */
export function isSaProvince(value: unknown): value is SaProvince {
  return typeof value === 'string' && (SA_PROVINCES as readonly string[]).includes(value);
}

/**
 * Normalise an arbitrary region value (e.g. an unregenerated DTO field read
 * defensively, or a stale localStorage entry) to a canonical province or null.
 * Anything not in the list — '', whitespace, an old/renamed value — becomes
 * null so callers can treat "unset" uniformly.
 */
export function normalizeRegion(value: unknown): SaProvince | null {
  return isSaProvince(value) ? value : null;
}
